import { useCallback, useRef, useState } from 'react'

interface IConfirmationState<TData> {
  isVisible: boolean
  data: TData
}

// Require a `data` argument only when the confirmation carries a payload (TData !== void).
type TConfirmArgs<TData> = TData extends void ? [] : [data: TData]

/**
 * Promise-based confirmation primitive. `confirm(data)` opens the dialog and
 * resolves to `true` on confirm or `false` on cancel — behaving like native
 * `confirm()`. `data` carries any per-invocation context the dialog needs.
 */
export const useConfirmation = <TData = void>(initialData: TData = undefined as TData) => {
  const [state, setState] = useState<IConfirmationState<TData>>({
    isVisible: false,
    data: initialData,
  })
  const resolverRef = useRef<((confirmed: boolean) => void) | null>(null)

  const confirm = useCallback((...args: TConfirmArgs<TData>) => {
    setState({ isVisible: true, data: args[0] as TData })

    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve
    })
  }, [])

  const settle = useCallback((confirmed: boolean) => {
    setState((prev) => ({ ...prev, isVisible: false }))
    resolverRef.current?.(confirmed)
    resolverRef.current = null
  }, [])

  return {
    confirm,
    data: state.data,
    dialogProps: {
      isVisible: state.isVisible,
      onConfirm: () => settle(true),
      onReject: () => settle(false),
    },
  }
}
